import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  RefreshCw, Loader2, ChevronDown, ChevronRight,
  CheckCircle2, AlertTriangle, XCircle, Info, Search,
} from 'lucide-react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { diagnosticsAPI } from '../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/errors';

// ── Helpers ─────────────────────────────────────────────────────

function relativeTime(isoString) {
  if (!isoString) return '';
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return isoString;
  const now = new Date();
  const diffMs = now - date;
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 0) return 'just now';
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  return `${diffDay}d ago`;
}

function absoluteTime(isoString) {
  if (!isoString) return '';
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return isoString;
  return date.toLocaleString();
}

function isWithin24h(isoString) {
  if (!isoString) return false;
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return false;
  return (new Date() - date) < 24 * 60 * 60 * 1000;
}

function extractFromPayload(payload, ...keys) {
  if (!payload || typeof payload !== 'object') return null;
  for (const key of keys) {
    if (payload[key] != null) return String(payload[key]);
  }
  // Check nested data envelope
  const data = payload.data;
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    for (const key of keys) {
      if (data[key] != null) return String(data[key]);
    }
    const attrs = data.attributes;
    if (attrs && typeof attrs === 'object') {
      for (const key of keys) {
        if (attrs[key] != null) return String(attrs[key]);
      }
    }
  }
  return null;
}

// ── Status banner ───────────────────────────────────────────────

function StatusBanner({ logData, reservationData, routeData }) {
  // Not enough data yet
  if (!logData || !reservationData || !routeData) {
    return (
      <div className="rounded-lg border border-muted bg-muted/30 p-4 flex items-start gap-3">
        <Info className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
        <div>
          <p className="font-medium">Loading diagnostic data...</p>
          <p className="text-sm text-muted-foreground">
            Fetching from all three endpoints to determine pipeline status.
          </p>
        </div>
      </div>
    );
  }

  const hasLogs = logData.count > 0;
  const hasReservations = reservationData.count > 0;
  const webhookRoute = routeData.routes.find(
    (r) => r.path === '/api/uplisting/webhook' && r.methods.includes('POST')
  );
  const hasWebhookRoute = !!webhookRoute;

  const webhookReservations = reservationData.docs.filter(
    (d) => d.webhook_event != null
  );
  const hasRecentWebhookReservation = webhookReservations.some(
    (d) => isWithin24h(d.synced_at)
  );

  let color, icon, headline, detail;

  if (!hasWebhookRoute) {
    color = 'red';
    icon = <XCircle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5 shrink-0" />;
    headline = 'Webhook route not mounted';
    detail = 'POST /api/uplisting/webhook is missing from the route table. This is a deployment issue. Redeploy or check that the router is registered in server.py.';
  } else if (hasLogs && hasRecentWebhookReservation) {
    color = 'green';
    icon = <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5 shrink-0" />;
    headline = 'Webhook pipeline healthy';
    detail = 'Webhooks are arriving, parsing, and persisting to the reservations collection within the last 24h.';
  } else if (hasLogs && !hasRecentWebhookReservation) {
    color = 'yellow';
    icon = <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />;
    headline = 'Webhook arriving but not landing';
    detail = 'Webhook payloads are reaching the handler (log entries exist) but no reservation with a webhook_event has been written in the last 24h. The extraction or upsert step is likely failing. Check application logs for "webhook processing failed" errors.';
  } else if (!hasLogs && hasReservations) {
    color = 'yellow';
    icon = <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />;
    headline = 'Only scheduled sync working';
    detail = 'No webhook log entries exist, but reservations are present from the scheduled full sync. The webhook path is dead. Check whether UPLISTING_WEBHOOK_TOKEN is set and the registered URL includes the correct ?token= parameter.';
  } else {
    color = 'red';
    icon = <XCircle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5 shrink-0" />;
    headline = 'Webhook is not reaching the handler';
    detail = 'No webhook log entries and the route is mounted. Requests are being rejected before the handler (token mismatch) or Uplisting is not sending. Check whether UPLISTING_WEBHOOK_TOKEN is set in production env and the registered URL includes the correct ?token= parameter.';
  }

  const borderColor = {
    green: 'border-green-300 bg-green-50 dark:border-green-800 dark:bg-green-950',
    yellow: 'border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950',
    red: 'border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-950',
  }[color];

  return (
    <div className={`rounded-lg border p-4 flex items-start gap-3 ${borderColor}`}>
      {icon}
      <div>
        <p className="font-medium">{headline}</p>
        <p className="text-sm text-muted-foreground mt-1">{detail}</p>
      </div>
    </div>
  );
}

// ── Card 1: Webhook Log Tail ────────────────────────────────────

function WebhookLogCard({ data, setData }) {
  const [loading, setLoading] = useState(false);
  const [limit, setLimit] = useState('50');
  const [expanded, setExpanded] = useState({});

  const fetch = useCallback(async (lim) => {
    setLoading(true);
    try {
      const { data: result } = await diagnosticsAPI.webhookLogTail({ limit: lim });
      setData(result);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [setData]);

  useEffect(() => { fetch(50); }, [fetch]);

  const handleRefresh = () => fetch(Number(limit));
  const handleLimitChange = (val) => {
    setLimit(val);
    fetch(Number(val));
  };

  const entries = data?.entries ?? [];
  const mostRecent = entries.length > 0 ? entries[0].received_at : null;

  const toggleExpand = (idx) => {
    setExpanded((prev) => ({ ...prev, [idx]: !prev[idx] }));
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Recent webhook arrivals</CardTitle>
          <div className="flex items-center gap-2">
            <Select value={limit} onValueChange={handleLimitChange}>
              <SelectTrigger className="w-20 h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
                <SelectItem value="500">500</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={handleRefresh} disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            </Button>
          </div>
        </div>
        <p className="text-sm text-muted-foreground">
          {data == null
            ? 'Loading...'
            : entries.length > 0
              ? (
                <>
                  {data.count} {data.count === 1 ? 'entry' : 'entries'}, most recent{' '}
                  <span title={absoluteTime(mostRecent)}>{relativeTime(mostRecent)}</span>
                </>
              )
              : '0 entries \u2014 no webhook has reached the handler'}
        </p>
      </CardHeader>
      <CardContent>
        {entries.length === 0 && data != null && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950 p-3 text-sm">
            No webhook events recorded. Either Uplisting is not sending, the request is being
            rejected before the handler, or it is failing JSON parsing.
          </div>
        )}
        {entries.length > 0 && (
          <div className="overflow-x-auto border rounded-lg">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left px-3 py-2 font-medium w-8"></th>
                  <th className="text-left px-3 py-2 font-medium">Received</th>
                  <th className="text-left px-3 py-2 font-medium">Event</th>
                  <th className="text-left px-3 py-2 font-medium">Booking ID</th>
                  <th className="text-left px-3 py-2 font-medium">Property ID</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry, idx) => {
                  const payload = entry.payload ?? {};
                  const bookingId = extractFromPayload(payload, 'id') ?? '\u2014';
                  const propertyId = extractFromPayload(payload, 'property_id', 'listing_id') ?? '\u2014';
                  const isExpanded = expanded[idx];
                  return (
                    <React.Fragment key={idx}>
                      <tr className={`align-top group ${isExpanded ? '' : 'border-b last:border-0'}`}>
                        <td className="px-3 py-2">
                          <button
                            onClick={() => toggleExpand(idx)}
                            className="text-muted-foreground hover:text-foreground"
                          >
                            {isExpanded
                              ? <ChevronDown className="h-4 w-4" />
                              : <ChevronRight className="h-4 w-4" />}
                          </button>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap" title={absoluteTime(entry.received_at)}>
                          {relativeTime(entry.received_at)}
                        </td>
                        <td className="px-3 py-2">
                          <Badge variant="secondary">{entry.event}</Badge>
                        </td>
                        <td className="px-3 py-2 font-mono text-xs">{bookingId}</td>
                        <td className="px-3 py-2 font-mono text-xs">{propertyId}</td>
                      </tr>
                      {isExpanded && (
                        <tr className="border-b last:border-0">
                          <td colSpan={5} className="px-3 pb-3">
                            <pre className="text-xs bg-muted rounded p-3 overflow-x-auto max-h-64 overflow-y-auto whitespace-pre-wrap break-all">
                              {JSON.stringify(payload, null, 2)}
                            </pre>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Card 2: Recent Reservations ─────────────────────────────────

const FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'webhook', label: 'Via webhook only' },
  { value: 'sync', label: 'Via scheduled sync only' },
  { value: 'quarantined', label: 'Quarantined' },
];

function RecentReservationsCard({ data, setData }) {
  const [loading, setLoading] = useState(false);
  const [limit, setLimit] = useState('50');
  const [filter, setFilter] = useState('all');

  const fetch = useCallback(async (lim) => {
    setLoading(true);
    try {
      const { data: result } = await diagnosticsAPI.recentReservations({ limit: lim });
      setData(result);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [setData]);

  useEffect(() => { fetch(50); }, [fetch]);

  const handleRefresh = () => fetch(Number(limit));
  const handleLimitChange = (val) => {
    setLimit(val);
    fetch(Number(val));
  };

  const docs = data?.docs ?? [];
  const webhookCount = docs.filter((d) => d.webhook_event != null).length;
  const syncCount = docs.filter((d) => d.webhook_event == null).length;
  const mostRecent = docs.length > 0 ? docs[0].synced_at : null;

  const filtered = docs.filter((d) => {
    if (filter === 'webhook') return d.webhook_event != null;
    if (filter === 'sync') return d.webhook_event == null;
    if (filter === 'quarantined') return d.ingestion_status === 'quarantined';
    return true;
  });

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Recent reservations in DB</CardTitle>
          <div className="flex items-center gap-2">
            <Select value={limit} onValueChange={handleLimitChange}>
              <SelectTrigger className="w-20 h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
                <SelectItem value="500">500</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={handleRefresh} disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            </Button>
          </div>
        </div>
        <p className="text-sm text-muted-foreground">
          {data == null
            ? 'Loading...'
            : docs.length > 0
              ? (
                <>
                  {data.count} {data.count === 1 ? 'reservation' : 'reservations'}, most recent{' '}
                  <span title={absoluteTime(mostRecent)}>{relativeTime(mostRecent)}</span>
                  {' \u2014 '}
                  {webhookCount} via webhook, {syncCount} via scheduled sync
                </>
              )
              : '0 reservations in the database'}
        </p>
      </CardHeader>
      <CardContent>
        {/* Filter chips */}
        <div className="flex flex-wrap gap-2 mb-4">
          {FILTER_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setFilter(opt.value)}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                filter === opt.value
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background text-muted-foreground border-border hover:bg-muted'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {docs.length === 0 && data != null && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950 p-3 text-sm">
            No reservations in the database. Neither the webhook nor the scheduled sync has written anything.
          </div>
        )}

        {docs.length > 0 && (
          <div className="overflow-x-auto border rounded-lg">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left px-3 py-2 font-medium">Synced</th>
                  <th className="text-left px-3 py-2 font-medium">Uplisting ID</th>
                  <th className="text-left px-3 py-2 font-medium">Property</th>
                  <th className="text-left px-3 py-2 font-medium">Guest</th>
                  <th className="text-left px-3 py-2 font-medium">Check-in</th>
                  <th className="text-left px-3 py-2 font-medium">Check-out</th>
                  <th className="text-left px-3 py-2 font-medium">Status</th>
                  <th className="text-left px-3 py-2 font-medium">Ingestion</th>
                  <th className="text-left px-3 py-2 font-medium">Entity</th>
                  <th className="text-left px-3 py-2 font-medium">Source</th>
                  <th className="text-right px-3 py-2 font-medium">Price</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((doc, idx) => (
                  <tr key={idx} className="border-b last:border-0">
                    <td className="px-3 py-2 whitespace-nowrap text-xs" title={absoluteTime(doc.synced_at)}>
                      {relativeTime(doc.synced_at)}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{doc.uplisting_id ?? '\u2014'}</td>
                    <td className="px-3 py-2 text-xs">
                      {doc.property_name || doc.property_id || '\u2014'}
                    </td>
                    <td className="px-3 py-2 text-xs">{doc.guest_name || '\u2014'}</td>
                    <td className="px-3 py-2 text-xs whitespace-nowrap">{doc.check_in || '\u2014'}</td>
                    <td className="px-3 py-2 text-xs whitespace-nowrap">{doc.check_out || '\u2014'}</td>
                    <td className="px-3 py-2">
                      <Badge variant={doc.status === 'cancelled' ? 'destructive' : 'secondary'}>
                        {doc.status ?? '\u2014'}
                      </Badge>
                    </td>
                    <td className="px-3 py-2">
                      <Badge variant={doc.ingestion_status === 'quarantined' ? 'destructive' : 'secondary'}>
                        {doc.ingestion_status ?? '\u2014'}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-xs">{doc.entity_name || '\u2014'}</td>
                    <td className="px-3 py-2">
                      {doc.webhook_event
                        ? <Badge variant="default">{doc.webhook_event}</Badge>
                        : <span className="text-xs text-muted-foreground">sync</span>}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-xs whitespace-nowrap">
                      {doc.total_price != null
                        ? `${Number(doc.total_price).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${doc.currency || ''}`
                        : '\u2014'}
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={11} className="px-3 py-4 text-center text-sm text-muted-foreground">
                      No reservations match the selected filter.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Card 3: Route Introspection ─────────────────────────────────

function RouteIntrospectionCard({ data, setData }) {
  const [loading, setLoading] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const { data: result } = await diagnosticsAPI.routeIntrospection();
      setData(result);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [setData]);

  useEffect(() => { fetch(); }, [fetch]);

  const routes = data?.routes ?? [];
  const webhookRoute = routes.find(
    (r) => r.path === '/api/uplisting/webhook' && r.methods.includes('POST')
  );

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Mounted webhook routes</CardTitle>
          <Button variant="outline" size="sm" onClick={fetch} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {routes.length > 0 && (
          <div className="overflow-x-auto border rounded-lg">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left px-3 py-2 font-medium">Path</th>
                  <th className="text-left px-3 py-2 font-medium">Methods</th>
                  <th className="text-left px-3 py-2 font-medium">Function</th>
                  <th className="text-left px-3 py-2 font-medium">Module</th>
                </tr>
              </thead>
              <tbody>
                {routes.map((r, idx) => {
                  const isWebhook = r.path === '/api/uplisting/webhook' && r.methods.includes('POST');
                  return (
                    <tr key={idx} className={`border-b last:border-0 ${isWebhook ? 'bg-green-50/50 dark:bg-green-950/30' : ''}`}>
                      <td className="px-3 py-2 font-mono text-xs">{r.path}</td>
                      <td className="px-3 py-2 text-xs">{r.methods.join(', ')}</td>
                      <td className="px-3 py-2 font-mono text-xs">{r.endpoint_function}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">{r.module}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {data != null && !webhookRoute && (
          <div className="rounded-lg border border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-950 p-3 text-sm font-medium text-red-700 dark:text-red-400 flex items-center gap-2">
            <XCircle className="h-4 w-4 shrink-0" />
            Webhook route is NOT mounted. POST /api/uplisting/webhook is missing from the route table. This is a deployment issue.
          </div>
        )}
        {data != null && webhookRoute && (
          <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400 font-medium">
            <CheckCircle2 className="h-4 w-4" />
            POST /api/uplisting/webhook is mounted.
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Card 4: Per-property sync health ────────────────────────────

function daysSince(isoString) {
  if (!isoString) return null;
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return null;
  return Math.floor((new Date() - date) / (1000 * 60 * 60 * 24));
}

function FleetStalenessCard({ fleetData, onSelectProperty, selectedPropertyId }) {
  const [search, setSearch] = useState('');

  const rows = fleetData?.rows ?? [];
  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter(
      (r) =>
        (r.property_name || '').toLowerCase().includes(q) ||
        String(r.property_id).includes(q)
    );
  }, [rows, search]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Fleet-wide staleness</CardTitle>
        {fleetData && (
          <p className="text-sm text-muted-foreground">
            {fleetData.stale_count} of {fleetData.total_properties} properties stale
            {' '}(no sync in {fleetData.stale_cutoff_days}+ days)
            {fleetData.relative_stale_count > 0 && (
              <>, {fleetData.relative_stale_count} with a sync gap
                {' '}(&gt;{fleetData.relative_stale_cutoff_hours}h behind the fleet's own last sync)</>
            )}
          </p>
        )}
      </CardHeader>
      <CardContent>
        <div className="relative mb-3">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Filter by property name or ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-9 text-sm"
          />
        </div>
        {filtered.length > 0 && (
          <div className="overflow-x-auto border rounded-lg max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-background">
                <tr className="border-b bg-muted/50">
                  <th className="text-left px-3 py-2 font-medium">Property</th>
                  <th className="text-left px-3 py-2 font-medium">ID</th>
                  <th className="text-left px-3 py-2 font-medium">Entity</th>
                  <th className="text-left px-3 py-2 font-medium">Last synced</th>
                  <th className="text-right px-3 py-2 font-medium">Reservations</th>
                  <th className="text-center px-3 py-2 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => {
                  const isSelected = String(row.property_id) === String(selectedPropertyId);
                  return (
                    <tr
                      key={row.property_id}
                      onClick={() => onSelectProperty(String(row.property_id))}
                      className={`border-b last:border-0 cursor-pointer hover:bg-muted/30 ${
                        isSelected ? 'bg-primary/5' : ''
                      }`}
                    >
                      <td className="px-3 py-2 text-xs">{row.property_name || '\u2014'}</td>
                      <td className="px-3 py-2 font-mono text-xs">{row.property_id}</td>
                      <td className="px-3 py-2 text-xs">{row.entity_name || '\u2014'}</td>
                      <td className="px-3 py-2 text-xs whitespace-nowrap" title={absoluteTime(row.max_synced_at)}>
                        {row.max_synced_at ? relativeTime(row.max_synced_at) : 'never'}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-xs">{row.reservation_count}</td>
                      <td className="px-3 py-2 text-center">
                        <Badge variant={row.is_stale || row.relative_stale ? 'destructive' : 'secondary'}>
                          {row.is_stale ? 'stale' : row.relative_stale ? 'sync gap' : 'fresh'}
                        </Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {filtered.length === 0 && fleetData && (
          <p className="text-sm text-muted-foreground text-center py-4">
            {search ? 'No properties match the filter.' : 'No property data available.'}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function PropertyDetailCard({ propertyId, fleetData, onSelectProperty }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [propDocExpanded, setPropDocExpanded] = useState(false);

  const fetchDetail = useCallback(async (pid) => {
    if (!pid) return;
    setLoading(true);
    try {
      const { data: result } = await diagnosticsAPI.propertySyncState({ property_id: pid });
      setData(result);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (propertyId) fetchDetail(propertyId);
  }, [propertyId, fetchDetail]);

  // Compute fleet median from fleetData
  const fleetMedianDays = useMemo(() => {
    if (!fleetData?.rows?.length) return null;
    const withDates = fleetData.rows
      .filter((r) => r.max_synced_at)
      .map((r) => daysSince(r.max_synced_at))
      .filter((d) => d !== null)
      .sort((a, b) => a - b);
    if (!withDates.length) return null;
    const mid = Math.floor(withDates.length / 2);
    return withDates.length % 2 === 0
      ? Math.round((withDates[mid - 1] + withDates[mid]) / 2)
      : withDates[mid];
  }, [fleetData]);

  const propName = data?.property_doc?.name || propertyId;
  const propMaxSynced = data?.property_max_synced_at;
  const daysSinceSync = daysSince(propMaxSynced);
  const fleetRow = fleetData?.rows?.find((r) => String(r.property_id) === String(propertyId));
  const isRelativeStale = !!fleetRow?.relative_stale && !fleetRow?.is_stale;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CardTitle className="text-base shrink-0">Selected property:</CardTitle>
            {fleetData?.rows?.length > 0 ? (
              <Select value={propertyId} onValueChange={onSelectProperty}>
                <SelectTrigger className="w-64 h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-64">
                  {fleetData.rows.map((r) => (
                    <SelectItem key={r.property_id} value={String(r.property_id)}>
                      {r.property_name || r.property_id} ({r.property_id})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <span className="text-sm">{propName}</span>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchDetail(propertyId)}
            disabled={loading}
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading && !data && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading...
          </div>
        )}

        {data && (
          <>
            {/* Stat tiles */}
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Last synced</p>
                <p className="text-lg font-semibold" title={absoluteTime(propMaxSynced)}>
                  {propMaxSynced ? relativeTime(propMaxSynced) : 'never'}
                </p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Days since last sync</p>
                <p className="text-lg font-semibold">
                  {daysSinceSync !== null ? daysSinceSync : '\u2014'}
                </p>
              </div>
            </div>

            {/* Callout: earlier-warning relative-staleness signal (this property
                has fallen behind the fleet's own last sync by more than the
                gap threshold, well before the 7-day absolute cutoff below
                would ever fire -- this is what "2d ago while the fleet is
                1h ago" looks like before it accumulates into a week). */}
            {isRelativeStale && (
              <div className="rounded-lg border border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-950 p-3 text-sm">
                FETCH_FAILED (sync gap): this property is more than{' '}
                <strong>{fleetData?.relative_stale_cutoff_hours}h</strong> behind the fleet's
                own most recent sync ({relativeTime(fleetData?.fleet_max_synced_at)}), while
                other properties keep syncing on schedule. This is a per-property fetch
                failure, not a fleet-wide outage -- run the Live Uplisting probe below.
              </div>
            )}

            {/* Callout: absolute staleness (no sync at all in 7+ days) */}
            {daysSinceSync !== null && daysSinceSync > 7 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950 p-3 text-sm">
                Last sync was <strong>{daysSinceSync} days ago</strong>.
                {fleetMedianDays !== null && (
                  <> Fleet median is <strong>{fleetMedianDays} {fleetMedianDays === 1 ? 'day' : 'days'}</strong>.</>
                )}
              </div>
            )}

            {/* Property doc */}
            <div>
              <button
                onClick={() => setPropDocExpanded(!propDocExpanded)}
                className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground"
              >
                {propDocExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                uplisting_properties document
              </button>
              {propDocExpanded && data.property_doc && (
                <pre className="mt-2 text-xs bg-muted rounded p-3 overflow-x-auto max-h-64 overflow-y-auto whitespace-pre-wrap break-all">
                  {JSON.stringify(data.property_doc, null, 2)}
                </pre>
              )}
              {propDocExpanded && !data.property_doc && (
                <p className="mt-2 text-sm text-muted-foreground">No property document found for this ID.</p>
              )}
            </div>

            {/* Recent reservations */}
            <div>
              <p className="text-sm font-medium mb-2">
                Last {data.recent_reservations.length} reservations
                <span className="text-muted-foreground font-normal">
                  {' '}({data.reservation_count} total)
                </span>
              </p>
              {data.recent_reservations.length > 0 ? (
                <div className="overflow-x-auto border rounded-lg">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left px-3 py-2 font-medium">Uplisting ID</th>
                        <th className="text-left px-3 py-2 font-medium">Synced at</th>
                        <th className="text-left px-3 py-2 font-medium">Booked at</th>
                        <th className="text-left px-3 py-2 font-medium">Check-in</th>
                        <th className="text-left px-3 py-2 font-medium">Check-out</th>
                        <th className="text-left px-3 py-2 font-medium">Status</th>
                        <th className="text-left px-3 py-2 font-medium">Ingestion</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.recent_reservations.map((r, idx) => (
                        <tr key={idx} className="border-b last:border-0">
                          <td className="px-3 py-2 font-mono text-xs">{r.uplisting_id ?? '\u2014'}</td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap" title={absoluteTime(r.synced_at)}>
                            {relativeTime(r.synced_at)}
                          </td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap" title={absoluteTime(r.booked_at)}>
                            {r.booked_at ? relativeTime(r.booked_at) : '\u2014'}
                          </td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap">{r.check_in || '\u2014'}</td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap">{r.check_out || '\u2014'}</td>
                          <td className="px-3 py-2">
                            <Badge variant={r.status === 'cancelled' ? 'destructive' : 'secondary'}>
                              {r.status ?? '\u2014'}
                            </Badge>
                          </td>
                          <td className="px-3 py-2">
                            <Badge variant={r.ingestion_status === 'quarantined' ? 'destructive' : 'secondary'}>
                              {r.ingestion_status ?? '\u2014'}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No reservations found for this property.</p>
              )}
            </div>

            {/* Fleet comparison */}
            <div className="text-xs text-muted-foreground">
              Fleet max synced_at: {data.fleet_max_synced_at ? (
                <span title={absoluteTime(data.fleet_max_synced_at)}>{relativeTime(data.fleet_max_synced_at)}</span>
              ) : 'n/a'}
              {' | '}
              Property max synced_at: {propMaxSynced ? (
                <span title={absoluteTime(propMaxSynced)}>{relativeTime(propMaxSynced)}</span>
              ) : 'n/a'}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ── Card 5: Live Uplisting Probe ─────────────────────────────

// Pure interpretation function, exported so it has a direct recurrence-guard
// test independent of the component's render cycle. This is the surface
// that previously declared "Uplisting and our DB agree" when the live API
// returned 0 bookings and the DB held real records: gap = 0 - N <= 0 read
// as "no gap", and totalMissingCount is vacuously 0 over an empty bookings
// array. Both signals must be judged FALSE for a genuine agreement, and an
// empty-or-error live response for a property with existing DB docs must
// never reach that branch, regardless of what gap/totalMissingCount say.
export function getLiveProbeInterpretation(data, error, totalMissingCount, gap) {
  if (error) {
    const status = error?.response?.status;
    if (status && status >= 400) {
      return {
        color: 'red',
        text: `Uplisting's API rejected the probe for this property (HTTP ${status}). Possible causes: property delisted on Uplisting's side, API key permissions changed, property archived. Contact Uplisting support with this property ID.`,
      };
    }
    return {
      color: 'red',
      text: `Probe failed: ${getErrorMessage(error)}`,
    };
  }
  if (!data) return null;

  if (data.had_error || data.fetch_status === 'api_error') {
    return {
      color: 'red',
      text: `FETCH_FAILED: Uplisting's API rejected the probe for this property. HTTP status: ${data.raw_api_http_status ?? 'unknown'}. Possible causes: property delisted/disconnected on Uplisting's side, API key permissions or scope changed, property archived. Contact Uplisting support with this property ID.`,
    };
  }

  const dbCount = data.db_reservation_count ?? 0;
  if (data.fetch_status === 'empty_response_but_db_has_docs' || (data.bookings_returned === 0 && dbCount > 0)) {
    return {
      color: 'red',
      text: `FETCH_FAILED: Uplisting's live API returned 0 bookings for this property while our DB holds ${dbCount} record(s). This is not agreement -- a property does not lose its entire live booking history unless something changed on Uplisting's side (disconnected property, key re-scoped) or the fetch silently failed. Check "live_property_detail" and the raw response body on this card before concluding the data is genuinely gone.`,
    };
  }

  if (data.fetch_status === 'unexpected_response_shape') {
    return {
      color: 'red',
      text: 'EMPTY_RESPONSE: the API returned HTTP 200 with a response shape the sync code does not recognize (see response_shape.top_level_keys on this card). It is being silently coerced to "0 bookings" by the same parsing the real sync uses. Do not trust bookings_returned until this shape is confirmed against a real Uplisting payload.',
    };
  }

  if (totalMissingCount === 0 && gap <= 0) {
    return {
      color: 'green',
      text: 'Uplisting and our DB agree. The stale data is not a sync problem. Check whether the data you are comparing to (in Uplisting\'s web UI) is genuinely different from what the API returns; sometimes there is a delay between the UI and the API.',
    };
  }
  return {
    color: 'amber',
    text: `Our sync is missing ${totalMissingCount} booking${totalMissingCount !== 1 ? 's' : ''} that Uplisting's API currently returns. The scheduled sync is failing for this property at the ingestion or persistence layer. Next step: identify what these missing bookings have in common (status, channel, date range) and check the sync code's filter logic.`,
  };
}

function LiveProbeCard({ propertyId }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [probing, setProbing] = useState(false);
  const [rawExpanded, setRawExpanded] = useState(false);

  const handleProbe = useCallback(async () => {
    if (probing) return; // debounce guard
    setProbing(true);
    setLoading(true);
    setError(null);
    try {
      const { data: result } = await diagnosticsAPI.propertySyncProbe(propertyId);
      setData(result);
    } catch (err) {
      setError(err);
      setData(null);
    } finally {
      setLoading(false);
      // Re-enable after 2s to prevent spam
      setTimeout(() => setProbing(false), 2000);
    }
  }, [propertyId, probing]);

  // Reset when property changes
  useEffect(() => {
    setData(null);
    setError(null);
  }, [propertyId]);

  // Prepare Uplisting bookings sorted by check_in desc, limited to 50
  const uplBookings = useMemo(() => {
    if (!data?.bookings) return [];
    return [...data.bookings]
      .sort((a, b) => (b.check_in || '').localeCompare(a.check_in || ''))
      .slice(0, 50);
  }, [data]);

  // DB reservations (already sorted/limited by backend)
  const dbReservations = data?.db_reservations ?? [];
  const dbIdSet = useMemo(() => new Set(data?.db_uplisting_ids ?? []), [data]);

  const gap = data ? data.bookings_returned - (data.db_reservation_count ?? 0) : 0;

  // Count missing across ALL Uplisting bookings (not just the 50 displayed)
  const totalMissingCount = useMemo(() => {
    if (!data?.bookings) return 0;
    return data.bookings.filter((b) => !dbIdSet.has(String(b.id))).length;
  }, [data, dbIdSet]);

  const interpretation = data || error
    ? getLiveProbeInterpretation(data, error, totalMissingCount, gap)
    : null;

  const interpColors = {
    green: 'border-green-300 bg-green-50 dark:border-green-800 dark:bg-green-950 text-green-800 dark:text-green-300',
    amber: 'border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950 text-amber-800 dark:text-amber-300',
    red: 'border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-950 text-red-800 dark:text-red-300',
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Live Uplisting probe</CardTitle>
        <p className="text-sm text-muted-foreground">
          Fetches what Uplisting has for this property right now, and compares it to what's
          in our database. Use this to determine whether the gap is on Uplisting's side or ours.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-3">
          <Button onClick={handleProbe} disabled={loading || probing} size="sm">
            {loading ? (
              <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Probing...</>
            ) : (
              'Probe Uplisting now'
            )}
          </Button>
          <span className="text-xs text-muted-foreground">
            Property: {propertyId}
          </span>
        </div>

        {/* Caveat banner */}
        <div className="rounded-lg border border-muted bg-muted/30 p-3 text-xs text-muted-foreground flex items-start gap-2">
          <Info className="h-4 w-4 shrink-0 mt-0.5" />
          The Uplisting probe makes a live API call. Don't spam-click; each click hits Uplisting's API.
        </div>

        {/* Stat tiles */}
        {data && (
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-lg border p-3">
              <p className="text-xs text-muted-foreground">In Uplisting (live)</p>
              <p className="text-2xl font-semibold">{data.bookings_returned}</p>
            </div>
            <div className="rounded-lg border p-3">
              <p className="text-xs text-muted-foreground">In our DB</p>
              <p className="text-2xl font-semibold">{data.db_reservation_count ?? 0}</p>
            </div>
            <div className={`rounded-lg border p-3 ${gap !== 0 ? 'border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-950' : ''}`}>
              <p className="text-xs text-muted-foreground">Gap</p>
              <p className={`text-2xl font-semibold ${gap !== 0 ? 'text-red-600 dark:text-red-400' : ''}`}>
                {gap > 0 ? `+${gap}` : gap}
              </p>
            </div>
          </div>
        )}

        {/* Tables */}
        {data && !data.had_error && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Left: Uplisting (live) */}
            <div>
              <p className="text-sm font-medium mb-2">
                Uplisting (live)
                <span className="text-muted-foreground font-normal"> ({uplBookings.length} shown)</span>
              </p>
              <div className="overflow-x-auto border rounded-lg max-h-96 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-background">
                    <tr className="border-b bg-muted/50">
                      <th className="text-left px-2 py-1.5 font-medium text-xs">ID</th>
                      <th className="text-left px-2 py-1.5 font-medium text-xs">Check-in</th>
                      <th className="text-left px-2 py-1.5 font-medium text-xs">Check-out</th>
                      <th className="text-left px-2 py-1.5 font-medium text-xs">Status</th>
                      <th className="text-left px-2 py-1.5 font-medium text-xs">Guest</th>
                      <th className="text-right px-2 py-1.5 font-medium text-xs">Price</th>
                      <th className="text-center px-2 py-1.5 font-medium text-xs">Match</th>
                    </tr>
                  </thead>
                  <tbody>
                    {uplBookings.map((b) => {
                      const bid = String(b.id);
                      const missing = !dbIdSet.has(bid);
                      return (
                        <tr key={bid} className={`border-b last:border-0 ${missing ? 'bg-red-50 dark:bg-red-950/40' : ''}`}>
                          <td className="px-2 py-1.5 font-mono text-xs">{bid}</td>
                          <td className="px-2 py-1.5 text-xs whitespace-nowrap">{b.check_in || '\u2014'}</td>
                          <td className="px-2 py-1.5 text-xs whitespace-nowrap">{b.check_out || '\u2014'}</td>
                          <td className="px-2 py-1.5">
                            <Badge variant={b.status === 'cancelled' ? 'destructive' : 'secondary'} className="text-xs">
                              {b.status || '\u2014'}
                            </Badge>
                          </td>
                          <td className="px-2 py-1.5 text-xs">{b.guest_name || '\u2014'}</td>
                          <td className="px-2 py-1.5 text-right font-mono text-xs whitespace-nowrap">
                            {b.gross_revenue != null
                              ? Number(b.gross_revenue).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                              : '\u2014'}
                          </td>
                          <td className="px-2 py-1.5 text-center">
                            {missing ? (
                              <Badge variant="destructive" className="text-xs">MISSING IN DB</Badge>
                            ) : (
                              <CheckCircle2 className="h-3.5 w-3.5 text-green-600 dark:text-green-400 inline-block" />
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {uplBookings.length === 0 && (
                      <tr>
                        <td colSpan={7} className="px-2 py-4 text-center text-xs text-muted-foreground">
                          Uplisting returned 0 bookings for this property.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Right: In our DB */}
            <div>
              <p className="text-sm font-medium mb-2">
                In our DB
                <span className="text-muted-foreground font-normal"> ({dbReservations.length} shown)</span>
              </p>
              <div className="overflow-x-auto border rounded-lg max-h-96 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-background">
                    <tr className="border-b bg-muted/50">
                      <th className="text-left px-2 py-1.5 font-medium text-xs">ID</th>
                      <th className="text-left px-2 py-1.5 font-medium text-xs">Check-in</th>
                      <th className="text-left px-2 py-1.5 font-medium text-xs">Check-out</th>
                      <th className="text-left px-2 py-1.5 font-medium text-xs">Status</th>
                      <th className="text-left px-2 py-1.5 font-medium text-xs">Guest</th>
                      <th className="text-right px-2 py-1.5 font-medium text-xs">Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dbReservations.map((r, idx) => (
                      <tr key={r.uplisting_id || idx} className="border-b last:border-0">
                        <td className="px-2 py-1.5 font-mono text-xs">{r.uplisting_id || '\u2014'}</td>
                        <td className="px-2 py-1.5 text-xs whitespace-nowrap">{r.check_in || '\u2014'}</td>
                        <td className="px-2 py-1.5 text-xs whitespace-nowrap">{r.check_out || '\u2014'}</td>
                        <td className="px-2 py-1.5">
                          <Badge variant={r.status === 'cancelled' ? 'destructive' : 'secondary'} className="text-xs">
                            {r.status || '\u2014'}
                          </Badge>
                        </td>
                        <td className="px-2 py-1.5 text-xs">{r.guest_name || '\u2014'}</td>
                        <td className="px-2 py-1.5 text-right font-mono text-xs whitespace-nowrap">
                          {r.total_price != null
                            ? Number(r.total_price).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                            : '\u2014'}
                        </td>
                      </tr>
                    ))}
                    {dbReservations.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-2 py-4 text-center text-xs text-muted-foreground">
                          No reservations in our DB for this property.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Interpretation box */}
        {interpretation && (
          <div className={`rounded-lg border p-3 text-sm ${interpColors[interpretation.color]}`}>
            {interpretation.text}
          </div>
        )}

        {/* Raw request/response diagnostics -- what the interpretation above is based on */}
        {data && (
          <div>
            <button
              onClick={() => setRawExpanded(!rawExpanded)}
              className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground"
            >
              {rawExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              Raw request/response
            </button>
            {rawExpanded && (
              <div className="mt-2 space-y-3">
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">Request</p>
                  <pre className="text-xs bg-muted rounded p-3 overflow-x-auto whitespace-pre-wrap break-all">
                    {JSON.stringify(data.request, null, 2)}
                  </pre>
                </div>
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">
                    Raw response (HTTP {data.raw_api_http_status ?? 'unknown'}), first 2KB
                  </p>
                  <pre className="text-xs bg-muted rounded p-3 overflow-x-auto max-h-64 overflow-y-auto whitespace-pre-wrap break-all">
                    {data.raw_api_response_body_first_2kb || '(empty)'}
                  </pre>
                </div>
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">Response shape</p>
                  <pre className="text-xs bg-muted rounded p-3 overflow-x-auto whitespace-pre-wrap break-all">
                    {JSON.stringify(data.response_shape, null, 2)}
                  </pre>
                </div>
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">
                    Live property detail (GET /properties/{propertyId}, independent of our stored doc)
                  </p>
                  <pre className="text-xs bg-muted rounded p-3 overflow-x-auto max-h-64 overflow-y-auto whitespace-pre-wrap break-all">
                    {JSON.stringify(data.live_property_detail, null, 2)}
                  </pre>
                </div>
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">
                    Provenance (webhook vs pull, this property)
                  </p>
                  <pre className="text-xs bg-muted rounded p-3 overflow-x-auto whitespace-pre-wrap break-all">
                    {JSON.stringify(data.provenance, null, 2)}
                  </pre>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PropertySyncHealthSection() {
  const [fleetData, setFleetData] = useState(null);
  const [fleetLoading, setFleetLoading] = useState(false);
  const [selectedPropertyId, setSelectedPropertyId] = useState('194420'); // Default: Escargot Suite

  const fetchFleet = useCallback(async () => {
    setFleetLoading(true);
    try {
      const { data: result } = await diagnosticsAPI.lastSyncPerProperty();
      setFleetData(result);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setFleetLoading(false);
    }
  }, []);

  useEffect(() => { fetchFleet(); }, [fetchFleet]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold tracking-tight">Per-property sync health</h2>
          <p className="text-sm text-muted-foreground">
            Identifies properties where the scheduled sync has stopped writing new reservations
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchFleet} disabled={fleetLoading}>
          {fleetLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </Button>
      </div>
      <FleetStalenessCard
        fleetData={fleetData}
        onSelectProperty={setSelectedPropertyId}
        selectedPropertyId={selectedPropertyId}
      />
      <PropertyDetailCard
        propertyId={selectedPropertyId}
        fleetData={fleetData}
        onSelectProperty={setSelectedPropertyId}
      />
      <LiveProbeCard propertyId={selectedPropertyId} />
    </div>
  );
}

// ── BR OS Data Quality Section ───────────────────────────────

function fmt(n) {
  if (n == null) return '-';
  return Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function InterpretationBlock({ variant, children }) {
  const colors = {
    green: 'bg-green-50 border-green-200 text-green-800 dark:bg-green-950/30 dark:border-green-800 dark:text-green-300',
    amber: 'bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-950/30 dark:border-amber-800 dark:text-amber-300',
    red: 'bg-red-50 border-red-200 text-red-800 dark:bg-red-950/30 dark:border-red-800 dark:text-red-300',
    neutral: 'bg-slate-50 border-slate-200 text-slate-700 dark:bg-slate-900/30 dark:border-slate-700 dark:text-slate-300',
  };
  return (
    <div className={`mt-4 rounded-lg border p-3 text-sm ${colors[variant] || colors.neutral}`}>
      <span className="font-medium">Interpretation: </span>{children}
    </div>
  );
}

function LoadPrompt({ onClick, loading }) {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
      <p className="text-sm mb-3">Click to load diagnostic data</p>
      <Button variant="outline" size="sm" onClick={onClick} disabled={loading}>
        {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
        Load
      </Button>
    </div>
  );
}

function DiagCardHeader({ title, description, onRefresh, loading }) {
  return (
    <CardHeader>
      <div className="flex items-center justify-between">
        <CardTitle className="text-lg">{title}</CardTitle>
        <Button variant="outline" size="sm" onClick={onRefresh} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </Button>
      </div>
      <p className="text-sm text-muted-foreground">{description}</p>
    </CardHeader>
  );
}

function StatTile({ label, value, sub, className = '' }) {
  return (
    <div className={`rounded-lg border p-3 ${className}`}>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-xl font-semibold mt-1">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </div>
  );
}

function VatCheckCard() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: result } = await diagnosticsAPI.brosVatCheck();
      setData(result);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  const bs = data?.booking_summary;
  const is = data?.invoice_summary;

  // Interpretation logic: compare aggregate sums, but also note coverage ratio.
  // Not every booking has linked invoices, so aggregates may diverge structurally.
  // Check four comparisons: client_total vs total_amount, vs subtotal,
  // and (client_total - vat) vs total_amount, vs subtotal.
  let interpretation = null;
  if (bs && is && bs.count > 0 && is.count > 0) {
    const clientTotal = bs.total_client_total;
    const clientExVat = bs.total_client_ex_vat;
    const invoiceTotal = is.total_amount_sum;
    const invoiceSubtotal = is.subtotal_sum;
    const threshold = Math.max(clientTotal * 0.01, 1);

    if (Math.abs(clientTotal - invoiceTotal) < threshold) {
      interpretation = { variant: 'amber', text: 'client_total sum matches invoice total_amount sum (within 1%). This suggests client_total is VAT-INCLUSIVE. Turnover must subtract VAT to get net revenue.' };
    } else if (Math.abs(clientTotal - invoiceSubtotal) < threshold) {
      interpretation = { variant: 'green', text: 'client_total sum matches invoice subtotal sum (within 1%). This suggests client_total is VAT-NET (excludes tax). Use as-is for turnover.' };
    } else if (Math.abs(clientExVat - invoiceTotal) < threshold) {
      interpretation = { variant: 'green', text: '(client_total - vat) matches invoice total_amount (within 1%). client_total is VAT-NET after subtracting the vat field.' };
    } else {
      // Aggregates don't match, likely due to incomplete invoice coverage
      const coverageNote = `Coverage: ${bs.count} bookings, ${is.count} linked invoices. `;
      interpretation = { variant: 'neutral', text: `${coverageNote}Aggregate sums diverge (booking total: ${fmt(clientTotal)}, invoice total: ${fmt(invoiceTotal)}, invoice subtotal: ${fmt(invoiceSubtotal)}). Check the sample table below: if per-booking client_total matches invoice subtotal, the field is VAT-net; if it matches invoice total_amount, it is VAT-inclusive.` };
    }
  }

  return (
    <Card>
      <DiagCardHeader
        title="VAT Treatment Check"
        description="Cross-checks client_total against linked invoices to determine whether client_total is VAT-inclusive or VAT-net."
        onRefresh={fetchData}
        loading={loading}
      />
      <CardContent>
        {!data ? (
          <LoadPrompt onClick={fetchData} loading={loading} />
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <StatTile label="Sum client_total" value={fmt(bs.total_client_total)} sub={`${bs.count} bookings`} />
              <StatTile label="Sum (client_total - vat)" value={fmt(bs.total_client_ex_vat)} />
              <StatTile label="Sum invoice total_amount" value={fmt(is.total_amount_sum)} sub={`${is.count} invoices`} />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatTile label="Bookings with zero/null VAT" value={bs.zero_vat_count} />
              <StatTile label="Invoice subtotal sum" value={fmt(is.subtotal_sum)} />
              <StatTile label="Invoice tax_amount sum" value={fmt(is.tax_amount_sum)} />
              <StatTile label="Period start" value={bs.period_start} />
            </div>

            {data.samples?.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm border rounded-lg">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="px-3 py-2 text-left">Booking ID</th>
                      <th className="px-3 py-2 text-left">Client</th>
                      <th className="px-3 py-2 text-right">client_total</th>
                      <th className="px-3 py-2 text-right">VAT</th>
                      <th className="px-3 py-2 text-right">vat_rate</th>
                      <th className="px-3 py-2 text-right">Invoice total</th>
                      <th className="px-3 py-2 text-right">Invoice subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.samples.map((s, i) => {
                      const inv = s.linked_invoices?.[0];
                      return (
                        <tr key={i} className="border-b">
                          <td className="px-3 py-2 font-mono text-xs">{s.bros_id}</td>
                          <td className="px-3 py-2">{s.company || s.client_name || '-'}</td>
                          <td className="px-3 py-2 text-right">{fmt(s.client_total)}</td>
                          <td className="px-3 py-2 text-right">{fmt(s.vat)}</td>
                          <td className="px-3 py-2 text-right">{s.vat_rate != null ? `${s.vat_rate}%` : '-'}</td>
                          <td className="px-3 py-2 text-right">{inv ? fmt(inv.total_amount) : '-'}</td>
                          <td className="px-3 py-2 text-right">{inv ? fmt(inv.subtotal) : '-'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {interpretation && (
              <InterpretationBlock variant={interpretation.variant}>
                {interpretation.text}
              </InterpretationBlock>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function MarginConsistencyCard() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: result } = await diagnosticsAPI.brosMarginConsistency();
      setData(result);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  const c = data?.categories;

  return (
    <Card>
      <DiagCardHeader
        title="Margin Consistency"
        description="Checks whether margin == client_total - supplier_total for each booking. Categories: exact (<0.01), rounding (0.01-1.00), real discrepancy (>1.00)."
        onRefresh={fetchData}
        loading={loading}
      />
      <CardContent>
        {!data ? (
          <LoadPrompt onClick={fetchData} loading={loading} />
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatTile label="Exact match" value={c?.exact ?? 0} className="border-green-200 dark:border-green-800" />
              <StatTile label="Rounding (0.01-1.00)" value={c?.rounding ?? 0} className="border-amber-200 dark:border-amber-800" />
              <StatTile label="Real discrepancy (>1.00)" value={c?.real_discrepancy ?? 0} className="border-red-200 dark:border-red-800" />
              <StatTile label="Mean discrepancy" value={data.mean_discrepancy?.toFixed(4) ?? '-'} sub={`${data.total_bookings} bookings`} />
            </div>

            {data.examples?.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm border rounded-lg">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="px-3 py-2 text-left">Booking ID</th>
                      <th className="px-3 py-2 text-left">Client</th>
                      <th className="px-3 py-2 text-right">client_total</th>
                      <th className="px-3 py-2 text-right">supplier_total</th>
                      <th className="px-3 py-2 text-right">Stored margin</th>
                      <th className="px-3 py-2 text-right">Expected margin</th>
                      <th className="px-3 py-2 text-right">Discrepancy</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.examples.map((e, i) => (
                      <tr key={i} className="border-b">
                        <td className="px-3 py-2 font-mono text-xs">{e.bros_id}</td>
                        <td className="px-3 py-2">{e.company || e.client_name || '-'}</td>
                        <td className="px-3 py-2 text-right">{fmt(e.client_total)}</td>
                        <td className="px-3 py-2 text-right">{fmt(e.supplier_total)}</td>
                        <td className="px-3 py-2 text-right">{fmt(e.margin)}</td>
                        <td className="px-3 py-2 text-right">{fmt(e.expected_margin)}</td>
                        <td className="px-3 py-2 text-right font-medium text-red-600 dark:text-red-400">{fmt(e.discrepancy)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {c && (
              <InterpretationBlock variant={c.real_discrepancy === 0 ? 'green' : 'red'}>
                {c.real_discrepancy === 0
                  ? 'Margin field is reliable. Use stored margin directly.'
                  : `Margin field has ${c.real_discrepancy} real inconsistencies (discrepancy > 1.00). Recompute as client_total - supplier_total for canonical figures.`
                }
              </InterpretationBlock>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function StatusDistributionCard() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: result } = await diagnosticsAPI.brosStatusDistribution();
      setData(result);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  const renderStatusTable = (rows, columns) => (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border rounded-lg">
        <thead>
          <tr className="border-b bg-muted/50">
            {columns.map(c => (
              <th key={c.key} className={`px-3 py-2 ${c.align === 'right' ? 'text-right' : 'text-left'}`}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows?.map((r, i) => (
            <tr key={i} className="border-b">
              {columns.map(c => (
                <td key={c.key} className={`px-3 py-2 ${c.align === 'right' ? 'text-right' : ''}`}>
                  {c.key === 'status'
                    ? <Badge variant="outline">{r[c.key]}</Badge>
                    : c.fmt ? c.fmt(r[c.key]) : r[c.key]
                  }
                </td>
              ))}
            </tr>
          ))}
          {(!rows || rows.length === 0) && (
            <tr><td colSpan={columns.length} className="px-3 py-4 text-center text-muted-foreground">No data</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );

  const bookingCols = [
    { key: 'status', label: 'Status', align: 'left' },
    { key: 'count', label: 'Count', align: 'right' },
    { key: 'total_client_total', label: 'Sum client_total', align: 'right', fmt },
    { key: 'total_margin', label: 'Sum margin', align: 'right', fmt },
    { key: 'min_check_in', label: 'Earliest', align: 'left' },
    { key: 'max_check_in', label: 'Latest', align: 'left' },
  ];
  const invoiceCols = [
    { key: 'status', label: 'Status', align: 'left' },
    { key: 'count', label: 'Count', align: 'right' },
    { key: 'total_amount', label: 'Sum total_amount', align: 'right', fmt },
    { key: 'min_issue_date', label: 'Earliest', align: 'left' },
    { key: 'max_issue_date', label: 'Latest', align: 'left' },
  ];

  return (
    <Card>
      <DiagCardHeader
        title="Status Distribution"
        description="Groups bookings and invoices by status value. Decide which statuses are revenue-bearing before building the dashboard."
        onRefresh={fetchData}
        loading={loading}
      />
      <CardContent>
        {!data ? (
          <LoadPrompt onClick={fetchData} loading={loading} />
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <h4 className="text-sm font-medium mb-2">Booking statuses</h4>
                {renderStatusTable(data.bookings, bookingCols)}
              </div>
              <div>
                <h4 className="text-sm font-medium mb-2">Invoice statuses</h4>
                {renderStatusTable(data.invoices, invoiceCols)}
              </div>
            </div>
            <InterpretationBlock variant="neutral">
              Review these statuses and decide which are revenue-bearing. Cancelled/no-show statuses should be excluded from turnover calculations.
            </InterpretationBlock>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function CurrencyDistributionCard() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: result } = await diagnosticsAPI.brosCurrencyDistribution();
      setData(result);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  // Determine if single-currency
  let interpretation = null;
  if (data?.bookings?.length > 0) {
    const total = data.bookings.reduce((s, r) => s + r.count, 0);
    const top = data.bookings[0];
    const topPctNum = total > 0 ? (top.count / total * 100) : 0;
    const topPct = topPctNum.toFixed(1);
    if (topPctNum > 95) {
      interpretation = { variant: 'green', text: `Effectively single-currency: ${topPct}% of bookings are in ${top.currency}. FX conversion is edge-case only.` };
    } else {
      interpretation = { variant: 'amber', text: `Multi-currency: top currency (${top.currency}) accounts for only ${topPct}% of bookings. Dashboard numbers must convert to GBP using exchange_rates.` };
    }
  }

  return (
    <Card>
      <DiagCardHeader
        title="Currency Distribution"
        description="Groups bookings and invoices by currency to determine whether multi-currency FX handling is needed."
        onRefresh={fetchData}
        loading={loading}
      />
      <CardContent>
        {!data ? (
          <LoadPrompt onClick={fetchData} loading={loading} />
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <h4 className="text-sm font-medium mb-2">Bookings by currency</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border rounded-lg">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="px-3 py-2 text-left">Currency</th>
                        <th className="px-3 py-2 text-right">Count</th>
                        <th className="px-3 py-2 text-right">Sum client_total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.bookings.map((r, i) => (
                        <tr key={i} className="border-b">
                          <td className="px-3 py-2 font-mono">{r.currency}</td>
                          <td className="px-3 py-2 text-right">{r.count}</td>
                          <td className="px-3 py-2 text-right">{fmt(r.total_client_total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div>
                <h4 className="text-sm font-medium mb-2">Invoices by currency</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border rounded-lg">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="px-3 py-2 text-left">Currency</th>
                        <th className="px-3 py-2 text-right">Count</th>
                        <th className="px-3 py-2 text-right">Sum total_amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.invoices.map((r, i) => (
                        <tr key={i} className="border-b">
                          <td className="px-3 py-2 font-mono">{r.currency}</td>
                          <td className="px-3 py-2 text-right">{r.count}</td>
                          <td className="px-3 py-2 text-right">{fmt(r.total_amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            {interpretation && (
              <InterpretationBlock variant={interpretation.variant}>
                {interpretation.text}
              </InterpretationBlock>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ClientNamesCard() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [search, setSearch] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: result } = await diagnosticsAPI.brosClientNames();
      setData(result);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  const filteredRows = useMemo(() => {
    if (!data?.rows) return [];
    if (!search.trim()) return data.rows;
    const q = search.trim().toLowerCase();
    return data.rows.filter(
      r => (r.company || '').toLowerCase().includes(q) || (r.client_name || '').toLowerCase().includes(q)
    );
  }, [data, search]);

  const dupes = data?.potential_duplicates || [];

  return (
    <Card>
      <DiagCardHeader
        title="Client Name Consistency"
        description="Groups bookings by company and client_name. Detects case-insensitive alias collisions that would break per-client aggregation."
        onRefresh={fetchData}
        loading={loading}
      />
      <CardContent>
        {!data ? (
          <LoadPrompt onClick={fetchData} loading={loading} />
        ) : (
          <div className="space-y-4">
            {dupes.length > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-950/30 dark:border-amber-800 dark:text-amber-300">
                <span className="font-medium">{dupes.length} potential alias group{dupes.length !== 1 ? 's' : ''} detected:</span>
                <ul className="mt-1 ml-4 list-disc">
                  {dupes.map((d, i) => (
                    <li key={i}>{d.variants.join(' / ')} ({d.total_bookings} bookings)</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex items-center gap-2">
              <Search className="h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Filter by company or client name..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="max-w-sm"
              />
              <span className="text-xs text-muted-foreground">
                {filteredRows.length} of {data.rows?.length ?? 0} rows
              </span>
            </div>

            <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
              <table className="w-full text-sm border rounded-lg">
                <thead className="sticky top-0 bg-background">
                  <tr className="border-b bg-muted/50">
                    <th className="px-3 py-2 text-left">Company</th>
                    <th className="px-3 py-2 text-left">Client name</th>
                    <th className="px-3 py-2 text-right">Bookings</th>
                    <th className="px-3 py-2 text-right">Sum client_total</th>
                    <th className="px-3 py-2 text-right">Sum margin</th>
                    <th className="px-3 py-2 text-left">Last booking</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((r, i) => (
                    <tr key={i} className="border-b">
                      <td className="px-3 py-2">{r.company || <span className="text-muted-foreground">(empty)</span>}</td>
                      <td className="px-3 py-2">{r.client_name || <span className="text-muted-foreground">(empty)</span>}</td>
                      <td className="px-3 py-2 text-right">{r.count}</td>
                      <td className="px-3 py-2 text-right">{fmt(r.total_client_total)}</td>
                      <td className="px-3 py-2 text-right">{fmt(r.total_margin)}</td>
                      <td className="px-3 py-2">{r.max_check_in || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <InterpretationBlock variant={dupes.length > 0 ? 'amber' : 'green'}>
              {dupes.length > 0
                ? 'Review the alias groups above. If duplicates exist, a canonical client alias map is needed before per-client P&L is reliable.'
                : 'No case-insensitive duplicates detected. Company names appear consistent for per-client aggregation.'
              }
            </InterpretationBlock>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function BROSDataQualitySection() {
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold tracking-tight">BR OS Data Quality</h2>
        <p className="text-sm text-muted-foreground">
          Empirical answers to definitional questions, required before building the BR OS dashboard.
        </p>
      </div>
      <VatCheckCard />
      <MarginConsistencyCard />
      <StatusDistributionCard />
      <CurrencyDistributionCard />
      <ClientNamesCard />
    </div>
  );
}

// ── BR OS Sync Health Section ────────────────────────────────

function BROSSyncStateCard() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: result } = await diagnosticsAPI.brosSyncState();
      setData(result);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  const months = data?.months ?? [];
  const marMay = data?.mar_may_2026;

  return (
    <Card>
      <DiagCardHeader
        title="BR OS Sync State"
        description="Collection-level counts, quarantine breakdown, and month coverage for bros_bookings."
        onRefresh={fetchData}
        loading={loading}
      />
      <CardContent>
        {!data ? (
          <LoadPrompt onClick={fetchData} loading={loading} />
        ) : (
          <div className="space-y-4">
            {/* Staleness badge */}
            {data.is_stale ? (
              <div className="rounded-lg border border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-950 p-3 text-sm flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400 shrink-0" />
                <span>
                  <strong>Stale.</strong> max synced_at is{' '}
                  <span title={data.max_synced_at}>{relativeTime(data.max_synced_at)}</span>
                  {' '}(threshold: {data.stale_cutoff_days} days).
                </span>
              </div>
            ) : (
              <div className="rounded-lg border border-green-300 bg-green-50 dark:border-green-800 dark:bg-green-950 p-3 text-sm flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400 shrink-0" />
                <span>
                  Sync is fresh. max synced_at:{' '}
                  <span title={data.max_synced_at}>{relativeTime(data.max_synced_at)}</span>
                </span>
              </div>
            )}

            {/* Stat tiles */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatTile label="Bookings" value={data.bookings_count} />
              <StatTile label="Invoices" value={data.invoices_count} />
              <StatTile label="Properties" value={data.properties_count} />
              <StatTile label="Revenue-bearing" value={data.revenue_bearing_count} sub="confirmed/completed" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <StatTile label="Quarantined" value={data.quarantined_count}
                className={data.quarantined_count > 0 ? 'border-amber-200 dark:border-amber-800' : ''} />
              <StatTile label="Max synced_at" value={data.max_synced_at ? relativeTime(data.max_synced_at) : 'never'}
                sub={data.max_synced_at ? absoluteTime(data.max_synced_at) : ''} />
              <StatTile label="Last sync log" value={data.last_sync_log?.timestamp ? relativeTime(data.last_sync_log.timestamp) : 'none'}
                sub={data.last_sync_log?.timestamp ? absoluteTime(data.last_sync_log.timestamp) : ''} />
            </div>

            {/* Mar-May 2026 comparison window */}
            {marMay && (
              <div>
                <h4 className="text-sm font-medium mb-2">Mar-May 2026 window (comparison to BR OS screenshot)</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <StatTile label="Bookings (Mar-May)" value={marMay.total_count}
                    className={marMay.total_count === 0 ? 'border-red-300 dark:border-red-800' : ''} />
                  <StatTile label="Sum client_total" value={fmt(marMay.total_client_total)} />
                </div>
                {marMay.by_status?.length > 0 && (
                  <div className="mt-2 overflow-x-auto border rounded-lg">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-muted/50">
                          <th className="text-left px-3 py-2 font-medium">Status</th>
                          <th className="text-right px-3 py-2 font-medium">Count</th>
                          <th className="text-right px-3 py-2 font-medium">Sum client_total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {marMay.by_status.map((r, i) => (
                          <tr key={i} className="border-b last:border-0">
                            <td className="px-3 py-2"><Badge variant="outline">{r.status}</Badge></td>
                            <td className="px-3 py-2 text-right">{r.count}</td>
                            <td className="px-3 py-2 text-right">{fmt(r.sum_client_total)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Quarantine reasons */}
            {data.quarantine_reasons?.length > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-2">Quarantine reasons</h4>
                <div className="overflow-x-auto border rounded-lg">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left px-3 py-2 font-medium">Reason</th>
                        <th className="text-right px-3 py-2 font-medium">Count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.quarantine_reasons.map((r, i) => (
                        <tr key={i} className="border-b last:border-0">
                          <td className="px-3 py-2 text-xs">{r.reason}</td>
                          <td className="px-3 py-2 text-right">{r.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Month coverage */}
            <div>
              <h4 className="text-sm font-medium mb-2">
                Month coverage
                <span className="text-muted-foreground font-normal"> ({months.length} distinct months)</span>
              </h4>
              {months.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {months.map((m) => (
                    <span key={m.month}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md border text-xs font-mono"
                    >
                      {m.month}
                      <span className="text-muted-foreground">({m.count})</span>
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No bookings with check_in dates.</p>
              )}
            </div>

            {/* By status table */}
            {data.by_status?.length > 0 && (
              <details className="text-xs">
                <summary className="cursor-pointer text-sm font-medium text-muted-foreground">
                  Full status breakdown ({data.by_status.length} statuses)
                </summary>
                <div className="mt-2 overflow-x-auto border rounded-lg">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left px-3 py-2 font-medium">Status</th>
                        <th className="text-right px-3 py-2 font-medium">Count</th>
                        <th className="text-right px-3 py-2 font-medium">Sum client_total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.by_status.map((r, i) => (
                        <tr key={i} className="border-b last:border-0">
                          <td className="px-3 py-2"><Badge variant="outline">{r.status}</Badge></td>
                          <td className="px-3 py-2 text-right">{r.count}</td>
                          <td className="px-3 py-2 text-right">{fmt(r.sum_client_total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}

            {/* By ingestion_status table */}
            {data.by_ingestion?.length > 0 && (
              <details className="text-xs">
                <summary className="cursor-pointer text-sm font-medium text-muted-foreground">
                  Ingestion status breakdown ({data.by_ingestion.length} statuses)
                </summary>
                <div className="mt-2 overflow-x-auto border rounded-lg">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left px-3 py-2 font-medium">Ingestion status</th>
                        <th className="text-right px-3 py-2 font-medium">Count</th>
                        <th className="text-right px-3 py-2 font-medium">Sum client_total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.by_ingestion.map((r, i) => (
                        <tr key={i} className="border-b last:border-0">
                          <td className="px-3 py-2">
                            <Badge variant={r.ingestion_status === 'quarantined' ? 'destructive' : 'secondary'}>
                              {r.ingestion_status}
                            </Badge>
                          </td>
                          <td className="px-3 py-2 text-right">{r.count}</td>
                          <td className="px-3 py-2 text-right">{fmt(r.sum_client_total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function BROSLiveProbeCard() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [probing, setProbing] = useState(false);

  const handleProbe = useCallback(async () => {
    if (probing) return;
    setProbing(true);
    setLoading(true);
    setError(null);
    try {
      const { data: result } = await diagnosticsAPI.brosProbe();
      setData(result);
    } catch (err) {
      setError(err);
      setData(null);
    } finally {
      setLoading(false);
      setTimeout(() => setProbing(false), 2000);
    }
  }, [probing]);

  const apiBookings = data?.api_bookings ?? [];
  const dbBookings = data?.db_bookings ?? [];
  const missingInDbIds = useMemo(() => new Set(data?.missing_in_db_ids ?? []), [data]);

  const getInterpretation = () => {
    if (error) {
      return { color: 'red', text: `Probe failed: ${getErrorMessage(error)}` };
    }
    if (!data) return null;
    if (data.had_error) {
      return {
        color: 'red',
        text: `BR OS API error: ${data.api_error} (HTTP ${data.api_http_status ?? 'unknown'}). Check BROS_API_KEY and BROS_BASE_URL.`,
      };
    }
    if (data.missing_in_db_count === 0 && data.gap <= 0) {
      return {
        color: 'green',
        text: 'BR OS and our DB agree for the last 30 days. If the overall count is low, the problem is older data (needs backfill). If the overall count matches expectations, the sync is working.',
      };
    }
    return {
      color: 'amber',
      text: `${data.missing_in_db_count} booking${data.missing_in_db_count !== 1 ? 's' : ''} from BR OS are missing in our DB for the last 30 days. The sync is partial or broken for recent records.`,
    };
  };

  const interpretation = data || error ? getInterpretation() : null;

  const interpColors = {
    green: 'border-green-300 bg-green-50 dark:border-green-800 dark:bg-green-950 text-green-800 dark:text-green-300',
    amber: 'border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950 text-amber-800 dark:text-amber-300',
    red: 'border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-950 text-red-800 dark:text-red-300',
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">BR OS Live Probe</CardTitle>
        <p className="text-sm text-muted-foreground">
          Calls the BR OS API for all bookings, filters to the last 30 days, and compares to what is in our DB.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-3">
          <Button onClick={handleProbe} disabled={loading || probing} size="sm">
            {loading ? (
              <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Probing...</>
            ) : (
              'Probe BR OS now'
            )}
          </Button>
        </div>

        <div className="rounded-lg border border-muted bg-muted/30 p-3 text-xs text-muted-foreground flex items-start gap-2">
          <Info className="h-4 w-4 shrink-0 mt-0.5" />
          The probe makes a live API call to BR OS. Each click hits the BR OS API.
        </div>

        {/* Stat tiles */}
        {data && !data.had_error && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatTile label="BR OS total (all time)" value={data.api_total_fetched} />
            <StatTile label="BR OS (last 30d)" value={data.api_recent_count} />
            <StatTile label="In our DB (last 30d)" value={data.db_count} />
            <StatTile label="Missing in DB"
              value={data.missing_in_db_count}
              className={data.missing_in_db_count > 0 ? 'border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-950' : ''} />
          </div>
        )}

        {/* Side-by-side tables */}
        {data && !data.had_error && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Left: BR OS (live) */}
            <div>
              <p className="text-sm font-medium mb-2">
                BR OS (live)
                <span className="text-muted-foreground font-normal"> ({apiBookings.length} shown)</span>
              </p>
              <div className="overflow-x-auto border rounded-lg max-h-96 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-background">
                    <tr className="border-b bg-muted/50">
                      <th className="text-left px-2 py-1.5 font-medium text-xs">ID</th>
                      <th className="text-left px-2 py-1.5 font-medium text-xs">Client</th>
                      <th className="text-left px-2 py-1.5 font-medium text-xs">Check-in</th>
                      <th className="text-left px-2 py-1.5 font-medium text-xs">Status</th>
                      <th className="text-right px-2 py-1.5 font-medium text-xs">Total</th>
                      <th className="text-center px-2 py-1.5 font-medium text-xs">Match</th>
                    </tr>
                  </thead>
                  <tbody>
                    {apiBookings.map((b) => {
                      const missing = missingInDbIds.has(b.bros_id);
                      return (
                        <tr key={b.bros_id} className={`border-b last:border-0 ${missing ? 'bg-red-50 dark:bg-red-950/40' : ''}`}>
                          <td className="px-2 py-1.5 font-mono text-xs">{b.bros_id}</td>
                          <td className="px-2 py-1.5 text-xs">{b.company || b.client_name || '-'}</td>
                          <td className="px-2 py-1.5 text-xs whitespace-nowrap">{b.check_in || '-'}</td>
                          <td className="px-2 py-1.5">
                            <Badge variant="secondary" className="text-xs">{b.status || '-'}</Badge>
                          </td>
                          <td className="px-2 py-1.5 text-right font-mono text-xs whitespace-nowrap">
                            {b.client_total != null ? fmt(b.client_total) : '-'}
                          </td>
                          <td className="px-2 py-1.5 text-center">
                            {missing ? (
                              <Badge variant="destructive" className="text-xs">MISSING IN DB</Badge>
                            ) : (
                              <CheckCircle2 className="h-3.5 w-3.5 text-green-600 dark:text-green-400 inline-block" />
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {apiBookings.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-2 py-4 text-center text-xs text-muted-foreground">
                          No bookings from BR OS in the last 30 days.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Right: In our DB */}
            <div>
              <p className="text-sm font-medium mb-2">
                In our DB
                <span className="text-muted-foreground font-normal"> ({dbBookings.length} shown)</span>
              </p>
              <div className="overflow-x-auto border rounded-lg max-h-96 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-background">
                    <tr className="border-b bg-muted/50">
                      <th className="text-left px-2 py-1.5 font-medium text-xs">ID</th>
                      <th className="text-left px-2 py-1.5 font-medium text-xs">Client</th>
                      <th className="text-left px-2 py-1.5 font-medium text-xs">Check-in</th>
                      <th className="text-left px-2 py-1.5 font-medium text-xs">Status</th>
                      <th className="text-left px-2 py-1.5 font-medium text-xs">Ingestion</th>
                      <th className="text-right px-2 py-1.5 font-medium text-xs">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dbBookings.map((r) => (
                      <tr key={r.bros_id} className="border-b last:border-0">
                        <td className="px-2 py-1.5 font-mono text-xs">{r.bros_id}</td>
                        <td className="px-2 py-1.5 text-xs">{r.company || r.client_name || '-'}</td>
                        <td className="px-2 py-1.5 text-xs whitespace-nowrap">{r.check_in || '-'}</td>
                        <td className="px-2 py-1.5">
                          <Badge variant="secondary" className="text-xs">{r.status || '-'}</Badge>
                        </td>
                        <td className="px-2 py-1.5">
                          <Badge variant={r.ingestion_status === 'quarantined' ? 'destructive' : 'secondary'} className="text-xs">
                            {r.ingestion_status || '-'}
                          </Badge>
                        </td>
                        <td className="px-2 py-1.5 text-right font-mono text-xs whitespace-nowrap">
                          {r.client_total != null ? fmt(r.client_total) : '-'}
                        </td>
                      </tr>
                    ))}
                    {dbBookings.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-2 py-4 text-center text-xs text-muted-foreground">
                          No bookings in our DB for the last 30 days.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Interpretation */}
        {interpretation && (
          <div className={`rounded-lg border p-3 text-sm ${interpColors[interpretation.color]}`}>
            {interpretation.text}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function BROSSyncHealthSection() {
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold tracking-tight">BR OS Sync Health</h2>
        <p className="text-sm text-muted-foreground">
          Determines whether Finance Agent's bros_bookings collection is in sync with BR OS.
        </p>
      </div>
      <BROSSyncStateCard />
      <BROSLiveProbeCard />
    </div>
  );
}

// ── Page ────────────────────────────────────────────────────────

export function UplistingWebhookDiagnosticPage() {
  const [logData, setLogData] = useState(null);
  const [reservationData, setReservationData] = useState(null);
  const [routeData, setRouteData] = useState(null);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Data Pipeline Diagnostic</h1>
          <p className="text-muted-foreground mt-1">
            Pipeline health checks and data quality audits
          </p>
        </div>

        <StatusBanner logData={logData} reservationData={reservationData} routeData={routeData} />

        <PropertySyncHealthSection />

        <WebhookLogCard data={logData} setData={setLogData} />
        <RecentReservationsCard data={reservationData} setData={setReservationData} />
        <RouteIntrospectionCard data={routeData} setData={setRouteData} />

        <BROSSyncHealthSection />

        <BROSDataQualitySection />
      </div>
    </DashboardLayout>
  );
}
